import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import type { IUser } from "../../../types/iuser";
import useTranscript from "../../../hooks/useTranscript";
import type { ITranscription } from "../../../hooks/useTranscript";
import { prefersReducedMotion } from "@shadowcue/ui";

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
import useScriptTemplateForConversation from "../../../hooks/useScriptTemplateForConversation";
import useTranscriptFilters from "../../../hooks/useTranscriptFilters";
import {
  PageContainer,
  PageTopbar,
  PageHeader,
  ContentContainer,
  Mark,
  astryx,
  neutralTheme,
  Search,
  useDocumentTheme,
} from "@shadowcue/ui";

interface Props {
  currentUser: IUser;
}

const renderHighlighted = (text: string, query: string): React.ReactNode => {
  const trimmed = query.trim();
  if (!trimmed) return text;
  const re = new RegExp(`(${escapeRegExp(trimmed)})`, "i");
  const parts = text.split(re);
  return parts.map((part, i) =>
    re.test(part) ? <Mark key={i}>{part}</Mark> : <span key={i}>{part}</span>,
  );
};

const ConversationTranscript = ({ currentUser }: Props) => {
  const { id } = useParams<{ id: string }>();
  const mode = useDocumentTheme();

  const { lines, conversationName, scriptTemplateId, loading, error } =
    useTranscript(id, currentUser.authToken);

  const { template } = useScriptTemplateForConversation(
    scriptTemplateId,
    currentUser.authToken,
  );

  const {
    speakerFilter,
    setSpeakerFilter,
    sectionFilter,
    setSectionFilter,
    filteredLines,
    availableSpeakers,
    clearFilters,
    hasAnyFilter,
  } = useTranscriptFilters(lines, template);

  const [search, setSearch] = useState("");

  const searchedLines = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return filteredLines;
    return filteredLines.filter((line) => line.text.toLowerCase().includes(query));
  }, [filteredLines, search]);

  const lineRefs = useRef<Record<number, HTMLLIElement | null>>({});

  const [searchParams] = useSearchParams();
  const focusQuestionParam = searchParams.get("focusQuestion");
  const focusedQuestionRef = useRef<string | null>(null);
  const [focusedLineIds, setFocusedLineIds] = useState<Set<number>>(new Set());

  // Deep-link from the conversation summary's "View in transcript" action:
  // find the run of lines for the focused question, scroll to it, and
  // highlight it. Runs once per focusQuestion, after lines have loaded. If
  // the question's lines are filtered/searched out, this is a graceful
  // no-op (no match found, nothing scrolls or highlights).
  useEffect(() => {
    if (!focusQuestionParam) return;
    if (focusedQuestionRef.current === focusQuestionParam) return;
    if (lines.length === 0) return;

    focusedQuestionRef.current = focusQuestionParam;

    const targetQuestionId = Number(focusQuestionParam);
    const run = searchedLines.filter(
      (line: ITranscription) => line.scriptTemplateQuestionId === targetQuestionId,
    );
    if (run.length === 0) return;

    setFocusedLineIds(new Set(run.map((line) => line.id)));

    const firstEl = lineRefs.current[run[0].id];
    if (firstEl) {
      firstEl.scrollIntoView({
        behavior: prefersReducedMotion() ? "auto" : "smooth",
        block: "center",
      });
    }
  }, [focusQuestionParam, lines, searchedLines]);

  const breadcrumbs = [
    { label: "Conversations", href: "/dashboard/conversations" },
    { label: conversationName || "…", href: `/dashboard/conversations/${id}` },
    { label: "Transcript" },
  ];

  const sections = template?.scriptTemplateSections ?? [];

  const speakerOptions = [
    { value: "all", label: "All speakers" },
    ...availableSpeakers.map((speaker) => ({
      value: String(speaker),
      label: `Speaker ${speaker}`,
    })),
  ];

  const sectionOptions = [
    { value: "all", label: "All sections" },
    ...sections
      .filter((section) => section.id !== undefined)
      .map((section) => ({ value: String(section.id), label: section.name })),
    { value: "offScript", label: "Off script" },
  ];

  const isNarrowing = hasAnyFilter || search.trim().length > 0;
  const narrowedEmpty = lines.length > 0 && isNarrowing && searchedLines.length === 0;

  const clearAllFilters = () => {
    clearFilters();
    setSearch("");
  };

  return (
    <astryx.Theme theme={neutralTheme} mode={mode}>
      <PageContainer>
        <PageTopbar breadcrumbs={breadcrumbs} />

        <ContentContainer loading={loading}>
          {error ? (
            <astryx.EmptyState title="Could not load transcript." />
          ) : lines.length === 0 ? (
            <astryx.EmptyState title="No transcript available for this conversation yet." />
          ) : (
            <astryx.VStack gap={6}>
              <PageHeader
                title="Transcript"
                description="Full conversation log. Search to find moments."
              />

              <astryx.HStack gap={3} align="center" wrap="wrap">
                <astryx.StackItem size="fill">
                  <astryx.TextInput
                    label="Search transcript"
                    isLabelHidden
                    startIcon={<astryx.Icon icon={Search} />}
                    placeholder="Search transcript…"
                    hasClear
                    width="100%"
                    value={search}
                    onChange={(value: string) => setSearch(value)}
                  />
                </astryx.StackItem>

                <astryx.Selector
                  label="Filter by speaker"
                  isLabelHidden
                  value={speakerFilter === null ? "all" : String(speakerFilter)}
                  options={speakerOptions}
                  onChange={(value: string) =>
                    setSpeakerFilter(value === "all" ? null : Number(value))
                  }
                />

                <astryx.Selector
                  label="Filter by section"
                  isLabelHidden
                  value={sectionFilter === null ? "all" : String(sectionFilter)}
                  options={sectionOptions}
                  onChange={(value: string) =>
                    setSectionFilter(
                      value === "all"
                        ? null
                        : value === "offScript"
                          ? "offScript"
                          : Number(value),
                    )
                  }
                />
              </astryx.HStack>

              {narrowedEmpty ? (
                <astryx.EmptyState
                  title="No lines match"
                  description="Try a different search or clear your filters."
                  actions={
                    <astryx.Button label="Clear filters" onClick={clearAllFilters} />
                  }
                />
              ) : (
                <astryx.List hasDividers>
                  {searchedLines.map((line) => (
                    <astryx.ListItem
                      key={line.id}
                      ref={(el: HTMLLIElement | null) => {
                        lineRefs.current[line.id] = el;
                      }}
                      isSelected={focusedLineIds.has(line.id)}
                      label={
                        <astryx.Text size="sm" color="secondary" weight="medium">
                          Speaker {line.speaker}
                        </astryx.Text>
                      }
                      description={
                        <astryx.Text type="body">
                          {renderHighlighted(line.text, search)}
                        </astryx.Text>
                      }
                    />
                  ))}
                </astryx.List>
              )}
            </astryx.VStack>
          )}
        </ContentContainer>
      </PageContainer>
    </astryx.Theme>
  );
};

export default ConversationTranscript;
